import React, { useState, useEffect } from 'react';
import { COLORS } from '../../utils/constants';
import { adminAPI } from '../../services/api';
import { useAdminToast } from '../../hooks/useAdminToast';
import { 
  MessageSquare, User, Clock, Send, X, 
  AlertCircle, CheckCircle 
} from 'lucide-react';

const SupportTickets = () => {
  const { toast } = useAdminToast();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    setLoading(true);
    try {
      // Get support tickets - Support sees only tickets from unassigned clients with 0 balance
      const res = await adminAPI.getTickets();
      
      // Filter tickets from new clients (those with no assigned_to)
      const filteredTickets = (res.data || []).filter(ticket => {
        // For Support role, backend should already filter
        return true;
      });
      
      setTickets(filteredTickets);
    } catch (err) {
      console.error('Failed to load tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    
    setSending(true);
    try {
      await adminAPI.replyToTicket(selectedTicket.id, replyText);
      setReplyText('');
      loadTickets();
      // Refresh selected ticket
      const updated = await adminAPI.getTicket(selectedTicket.id);
      setSelectedTicket(updated.data);
    } catch (err) {
      console.error('Failed to send reply:', err);
      toast.apiError(err, 'Ошибка при отправке ответа');
    } finally {
      setSending(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: COLORS.primary,
      in_progress: '#3b82f6',
      resolved: COLORS.success,
      closed: COLORS.textSecondary
    };
    return colors[status] || COLORS.textSecondary;
  };

  const getStatusLabel = (status) => {
    const labels = {
      open: 'Открыт',
      in_progress: 'В работе',
      resolved: 'Решён',
      closed: 'Закрыт'
    };
    return labels[status] || status;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ color: COLORS.text, fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>
          Тикеты поддержки
        </h1>
        <p style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
          Обращения от новых клиентов (с нулевым балансом)
        </p>
      </div>

      <div style={{ display: 'flex', gap: '20px' }}>
        {/* Tickets List */}
        <div style={{
          flex: '0 0 400px',
          backgroundColor: COLORS.card,
          borderRadius: '12px',
          border: `1px solid ${COLORS.border}`,
          overflow: 'hidden',
          maxHeight: 'calc(100vh - 200px)'
        }}>
          <div style={{
            padding: '16px',
            borderBottom: `1px solid ${COLORS.border}`,
            fontWeight: '600',
            color: COLORS.text
          }}>
            Все тикеты ({tickets.length})
          </div>

          <div style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 280px)' }}>
            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: COLORS.textSecondary }}>
                Загрузка...
              </div>
            ) : tickets.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <CheckCircle size={40} color={COLORS.success} style={{ marginBottom: '12px' }} />
                <div style={{ color: COLORS.textSecondary }}>Нет тикетов</div>
              </div>
            ) : (
              tickets.map(ticket => (
                <div
                  key={ticket.id}
                  onClick={() => setSelectedTicket(ticket)}
                  style={{
                    padding: '14px 16px',
                    borderBottom: `1px solid ${COLORS.border}20`,
                    cursor: 'pointer',
                    backgroundColor: selectedTicket?.id === ticket.id ? COLORS.background : 'transparent',
                    transition: 'background-color 0.2s'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ color: COLORS.text, fontWeight: '500', fontSize: '14px' }}>
                      {ticket.subject?.substring(0, 30) || 'Без темы'}...
                    </span>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '10px',
                      backgroundColor: `${getStatusColor(ticket.status)}20`,
                      color: getStatusColor(ticket.status),
                      fontSize: '11px',
                      fontWeight: '600'
                    }}>
                      {getStatusLabel(ticket.status)}
                    </span>
                  </div>
                  <div style={{ color: COLORS.textSecondary, fontSize: '12px', marginBottom: '4px' }}>
                    {ticket.user_email}
                  </div>
                  <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>
                    {formatDate(ticket.created_at)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Ticket Details */}
        <div style={{
          flex: 1,
          backgroundColor: COLORS.card,
          borderRadius: '12px',
          border: `1px solid ${COLORS.border}`,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 200px)'
        }}>
          {!selectedTicket ? (
            <div style={{ 
              flex: 1, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              flexDirection: 'column',
              color: COLORS.textSecondary
            }}>
              <MessageSquare size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <div>Выберите тикет для просмотра</div>
            </div>
          ) : (
            <>
              {/* Ticket Header */}
              <div style={{
                padding: '16px 20px',
                borderBottom: `1px solid ${COLORS.border}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start'
              }}>
                <div>
                  <h3 style={{ color: COLORS.text, fontSize: '16px', fontWeight: '600', margin: '0 0 8px 0' }}>
                    {selectedTicket.subject}
                  </h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <span style={{ color: COLORS.textSecondary, fontSize: '13px' }}>
                      <User size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                      {selectedTicket.user_email}
                    </span>
                    <span style={{ color: COLORS.textSecondary, fontSize: '13px' }}>
                      <Clock size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                      {formatDate(selectedTicket.created_at)}
                    </span>
                  </div>
                </div>
                <span style={{
                  padding: '4px 12px',
                  borderRadius: '20px',
                  backgroundColor: `${getStatusColor(selectedTicket.status)}20`,
                  color: getStatusColor(selectedTicket.status),
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  {getStatusLabel(selectedTicket.status)}
                </span>
              </div>

              {/* Messages */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                {/* Initial message */}
                <div style={{
                  backgroundColor: COLORS.background,
                  borderRadius: '12px',
                  padding: '14px',
                  marginBottom: '16px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    marginBottom: '8px' 
                  }}>
                    <span style={{ color: COLORS.text, fontWeight: '500', fontSize: '13px' }}>
                      {selectedTicket.user_email}
                    </span>
                    <span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
                      {formatDate(selectedTicket.created_at)}
                    </span>
                  </div>
                  <p style={{ color: COLORS.text, fontSize: '14px', margin: 0, lineHeight: '1.6' }}>
                    {selectedTicket.message}
                  </p>
                </div>

                {/* Replies */}
                {selectedTicket.messages?.map((msg, idx) => (
                  <div
                    key={idx}
                    style={{
                      backgroundColor: msg.sender_role !== 'user' ? `${COLORS.primary}10` : COLORS.background,
                      borderRadius: '12px',
                      padding: '14px',
                      marginBottom: '12px',
                      marginLeft: msg.sender_role !== 'user' ? '20px' : '0',
                      marginRight: msg.sender_role === 'user' ? '20px' : '0'
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginBottom: '8px' 
                    }}>
                      <span style={{ 
                        color: msg.sender_role !== 'user' ? COLORS.primary : COLORS.text, 
                        fontWeight: '500', 
                        fontSize: '13px' 
                      }}>
                        {msg.sender_role !== 'user' ? 'Поддержка' : selectedTicket.user_email}
                      </span>
                      <span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
                        {formatDate(msg.created_at)}
                      </span>
                    </div>
                    <p style={{ color: COLORS.text, fontSize: '14px', margin: 0, lineHeight: '1.6' }}>
                      {msg.content}
                    </p>
                  </div>
                ))}
              </div>

              {/* Reply Input */}
              {selectedTicket.status !== 'closed' && (
                <div style={{
                  padding: '16px 20px',
                  borderTop: `1px solid ${COLORS.border}`,
                  display: 'flex',
                  gap: '12px'
                }}>
                  <input
                    type="text"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Введите ответ..."
                    onKeyPress={(e) => e.key === 'Enter' && handleReply()}
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      backgroundColor: COLORS.background,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '8px',
                      color: COLORS.text,
                      fontSize: '14px',
                      outline: 'none'
                    }}
                  />
                  <button
                    onClick={handleReply}
                    disabled={!replyText.trim() || sending}
                    style={{
                      padding: '12px 20px',
                      backgroundColor: !replyText.trim() || sending ? COLORS.border : COLORS.primary,
                      border: 'none',
                      borderRadius: '8px',
                      color: !replyText.trim() || sending ? COLORS.textSecondary : '#000',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: !replyText.trim() || sending ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Send size={16} />
                    {sending ? 'Отправка...' : 'Отправить'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportTickets;
